import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { createConsultation } from '../api/easyworks';
import { validateConsultation, hasErrors } from '../lib/validators';

/**
 * Compact right-rail consultation form used across service pages.
 * Tags each submission with a category + subtype so backend routes it
 * to the correct Google Sheet tab.
 */
const QuoteForm = ({
  category = 'general',
  subtype = '',
  service = 'General',
  title = 'Get a free quote',
  subtitle = 'A specialist will reach out within 24 hours.',
  ctaLabel = 'Get Free Quote',
  testId = 'quote-form',
}) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ company: '', name: '', phone: '', email: '', website: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validateConsultation(form);
    if (hasErrors(found)) { setErrors(found); return; }
    setErrors({});
    setSubmitting(true);
    try {
      await createConsultation({
        ...form,
        service,
        category,
        subtype,
        source: 'sidebar',
      });
      toast({ title: 'Consultation requested!', description: 'Our expert will reach out within 24 hours.' });
      setForm({ company: '', name: '', phone: '', email: '', website: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={testId} className="bg-white border border-black/5 rounded-2xl shadow-md p-6 lg:sticky lg:top-24">
      <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-2">{title}</div>
      <p className="text-sm text-neutral-600 mb-5">{subtitle}</p>
      <div className="space-y-3">
        {/* Honeypot: hidden from real users, filled by bots. Absolute position so it can't get keyboard focus visually. */}
        <input
          type="text"
          name="website"
          tabIndex="-1"
          autoComplete="off"
          value={form.website}
          onChange={(e)=>update('website', e.target.value)}
          aria-hidden="true"
          style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', opacity: 0 }}
        />
        <input
          value={form.company}
          onChange={(e)=>update('company', e.target.value)}
          data-testid={`${testId}-company`}
          className="w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border border-transparent focus:border-[#F39200] focus:bg-white outline-none text-sm"
          placeholder="Company name"
        />
        <div>
          <input
            value={form.name}
            onChange={(e)=>update('name', e.target.value)}
            aria-invalid={!!errors.name}
            data-testid={`${testId}-name`}
            className={`w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border focus:bg-white outline-none text-sm ${errors.name ? 'border-red-500 focus:border-red-500' : 'border-transparent focus:border-[#F39200]'}`}
            placeholder="Your name *"
          />
          {errors.name && <p data-testid={`${testId}-error-name`} className="mt-1 text-xs text-red-600">{errors.name}</p>}
        </div>
        <div>
          <input
            value={form.phone}
            onChange={(e)=>update('phone', e.target.value)}
            aria-invalid={!!errors.phone}
            data-testid={`${testId}-phone`}
            className={`w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border focus:bg-white outline-none text-sm ${errors.phone ? 'border-red-500 focus:border-red-500' : 'border-transparent focus:border-[#F39200]'}`}
            placeholder="Phone number *"
          />
          {errors.phone && <p data-testid={`${testId}-error-phone`} className="mt-1 text-xs text-red-600">{errors.phone}</p>}
        </div>
        <div>
          <input
            value={form.email}
            onChange={(e)=>update('email', e.target.value)}
            aria-invalid={!!errors.email}
            data-testid={`${testId}-email`}
            className={`w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border focus:bg-white outline-none text-sm ${errors.email ? 'border-red-500 focus:border-red-500' : 'border-transparent focus:border-[#F39200]'}`}
            placeholder="Email (optional)"
          />
          {errors.email && <p data-testid={`${testId}-error-email`} className="mt-1 text-xs text-red-600">{errors.email}</p>}
        </div>
        {subtype && (
          <div className="text-[11px] text-neutral-500 flex items-center gap-1 bg-[#FAF2E8] rounded-md px-2 py-1.5">
            Interested in <span className="font-semibold text-neutral-800">{subtype}</span>
          </div>
        )}
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid={`${testId}-submit`}
        className="mt-5 w-full h-11 rounded-lg bg-[#F39200] hover:bg-[#E08400] disabled:opacity-60 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
      >
        {submitting ? 'Submitting…' : (<>{ctaLabel} <ArrowRight size={16} /></>)}
      </button>
      <p className="mt-3 text-[11px] text-neutral-400 text-center">No spam. We reply within 24 hrs on business days.</p>
    </form>
  );
};

export default QuoteForm;
